// 📁 frontend/src/pages/AdminCategoriesPage.jsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  fetchCategories,
  addCategory,
  updateCategory,
  deleteCategory
} from '../api/api';

export default function AdminCategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({ name: '', image: null });
  const [editingId, setEditingId] = useState(null);
  const [toastMessage, setToastMessage] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false); // ✅ For custom delete confirm
  const [selectedDeleteId, setSelectedDeleteId] = useState(null); // ✅ Store category ID to delete

  const showToast = (message) => {
    setToastMessage(message);
    setTimeout(() => setToastMessage(''), 2500);
  };

  const loadCategories = useCallback(async () => {
    const data = await fetchCategories();
    setCategories(data);
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  function handleInputChange(e) {
    const { name, value, files } = e.target;
    setForm({
      ...form,
      [name]: files ? files[0] : value
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const formData = new FormData();
    formData.append('name', form.name);
    if (form.image) formData.append('image', form.image);

    try {
      if (editingId) {
        await updateCategory(editingId, formData);
        showToast('✅ تم تعديل الفئة بنجاح');
      } else {
        await addCategory(formData);
        showToast('✅ تم إضافة الفئة بنجاح');
      }
      setForm({ name: '', image: null });
      setEditingId(null);
      loadCategories();
    } catch {
      showToast('❌ حدث خطأ أثناء حفظ الفئة');
    }
  }

  function handleEdit(category) {
    setForm({ name: category.name, image: null });
    setEditingId(category.id);
  }

  function handleDeletePrompt(id) {
    setSelectedDeleteId(id);
    setShowDeleteConfirm(true); // ✅ Show delete confirmation modal
  }

  async function handleDeleteConfirmed() {
    try {
      await deleteCategory(selectedDeleteId);
      showToast('✅ تم حذف الفئة');
      loadCategories();
    } catch {
      showToast('❌ فشل في حذف الفئة');
    }
    setShowDeleteConfirm(false);
    setSelectedDeleteId(null);
  }

  return (
    <div style={styles.container}>
      {/* ✅ Toast */}
      {toastMessage && <div style={styles.toast}>{toastMessage}</div>}

      {/* ✅ Custom Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <p style={styles.modalMessage}>⚠️ هل أنت متأكد من حذف الفئة؟</p>
            <div style={styles.modalActions}>
              <button
                onClick={handleDeleteConfirmed}
                style={styles.confirmButton}
              >
                ✅ تأكيد
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                style={styles.cancelButton}
              >
                ❌ إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Header */}
      <header style={styles.header}>
        <h1 style={styles.pageTitle}>📁 إدارة الفئات</h1>
      </header>

      {/* ✅ Summary Card */}
      <div style={styles.summaryCard}>
        <p style={styles.summaryValue}>{categories.length}</p>
        <p style={styles.summaryLabel}>📊 إجمالي الفئات</p>
      </div>

      {/* ✅ Form */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>
          {editingId ? '✏️ تعديل الفئة' : '➕ إضافة فئة جديدة'}
        </h3>
        <form onSubmit={handleSubmit} style={styles.form}>
          <input
            type="text"
            name="name"
            placeholder="📦 اسم الفئة"
            value={form.name}
            onChange={handleInputChange}
            required
            style={styles.input}
          />
          <input
            type="file"
            name="image"
            accept="image/*"
            onChange={handleInputChange}
            style={styles.input}
          />
          <button
            type="submit"
            style={{
              ...styles.submitButton,
              backgroundColor: editingId ? '#3390FF' : '#47AA52'
            }}
          >
            {editingId ? '💾 تحديث الفئة' : '➕ حفظ الفئة'}
          </button>
        </form>
      </div>

      {/* ✅ Category Cards */}
      <h3 style={styles.sectionTitle}>📋 قائمة الفئات</h3>
      <div style={styles.cardsContainer}>
        {categories.map((cat) => (
          <div key={cat.id} style={styles.cardItem}>
            <div style={styles.cardImageWrapper}>
              {cat.image ? (
                <img
                  src={`https://al-asala-restaurant.com/api/storage/${cat.image}`}
                  alt={cat.name}
                  style={styles.cardImage}
                />
              ) : (
                <div style={styles.noImage}>بدون صورة</div>
              )}
            </div>
            <h4 style={styles.cardName}>{cat.name}</h4>
            <div style={styles.cardActions}>
              <button
                onClick={() => handleEdit(cat)}
                style={styles.editButton}
              >
                ✏️ تعديل
              </button>
              <button
                onClick={() => handleDeletePrompt(cat.id)}
                style={styles.deleteButton}
              >
                🗑️ حذف
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* 🎨 Styles */
const styles = {
  container: {
    minHeight: '100vh',
    padding: '40px 20px',
    backgroundColor: '#FFF8F0',
    fontFamily: "'Cairo', sans-serif",
    direction: 'rtl'
  },
  header: {
    textAlign: 'center',
    marginBottom: '25px'
  },
  pageTitle: {
    fontSize: '26px',
    fontWeight: 800,
    color: '#4A3527'
  },
  toast: {
    position: 'fixed',
    top: '24px',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 999,
    padding: '10px 18px',
    backgroundColor: '#4BB543',
    color: '#FFF',
    borderRadius: '8px',
    fontSize: '14px',
    boxShadow: '0 5px 10px rgba(0,0,0,0.1)'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0,0,0,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modalContent: {
    backgroundColor: '#FFF',
    padding: '25px',
    borderRadius: '12px',
    width: '90%',
    maxWidth: '300px',
    textAlign: 'center',
    boxShadow: '0 5px 15px rgba(0,0,0,0.1)'
  },
  modalMessage: {
    marginBottom: '20px',
    fontSize: '16px',
    fontWeight: '600'
  },
  modalActions: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'center'
  },
  confirmButton: {
    backgroundColor: '#47AA52',
    padding: '8px 16px',
    borderRadius: '10px',
    border: 'none',
    color: '#fff',
    fontSize: '14px',
    cursor: 'pointer'
  },
  cancelButton: {
    backgroundColor: '#FF7272',
    padding: '8px 16px',
    borderRadius: '10px',
    border: 'none',
    color: '#fff',
    fontSize: '14px',
    cursor: 'pointer'
  },
  summaryCard: {
    textAlign: 'center',
    backgroundColor: '#FFF4E1',
    padding: '25px 15px',
    borderRadius: '15px',
    marginBottom: '30px',
    boxShadow: '0 6px 15px rgba(0,0,0,0.08)',
    border: '1px solid #F4DCC3',
    maxWidth: '300px',
    margin: '0 auto 30px'
  },
  summaryValue: {
    fontSize: '40px',
    fontWeight: 700,
    color: '#4A281A'
  },
  summaryLabel: {
    color: '#A06B47',
    fontSize: '16px'
  },
  card: {
    backgroundColor: '#FFF',
    padding: '20px',
    borderRadius: '12px',
    maxWidth: '500px',
    margin: '0 auto 30px',
    boxShadow: '0 6px 15px rgba(0,0,0,0.06)',
    border: '1px solid #E7D8C9'
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: 700,
    color: '#583B28',
    marginBottom: '20px'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px'
  },
  input: {
    padding: '12px 15px',
    borderRadius: '10px',
    border: '1px solid #DCC6B0',
    fontSize: '14px'
  },
  submitButton: {
    padding: '12px',
    borderRadius: '10px',
    border: 'none',
    color: '#FFF',
    fontWeight: '700',
    cursor: 'pointer',
    fontSize: '15px'
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#654833',
    textAlign: 'center',
    marginBottom: '15px'
  },
  cardsContainer: {
    display: 'grid',
    gap: '18px',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))'
  },
  cardItem: {
    backgroundColor: '#FFF',
    padding: '18px',
    borderRadius: '12px',
    boxShadow: '0 5px 12px rgba(0,0,0,0.05)',
    border: '1px solid #E7D8C9',
    textAlign: 'center'
  },
  cardImageWrapper: {
    height: '120px',
    borderRadius: '10px',
    overflow: 'hidden',
    marginBottom: '12px'
  },
  cardImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  noImage: {
    height: '100%',
    backgroundColor: '#EEE',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#777'
  },
  cardName: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#3E2B23',
    marginBottom: '12px'
  },
  cardActions: {
    display: 'flex',
    justifyContent: 'center',
    gap: '10px'
  },
  editButton: {
    backgroundColor: '#6AB6FF',
    padding: '6px 12px',
    borderRadius: '8px',
    border: 'none',
    color: '#FFF',
    cursor: 'pointer',
    fontSize: '14px'
  },
  deleteButton: {
    backgroundColor: '#FF7272',
    padding: '6px 12px',
    borderRadius: '8px',
    border: 'none',
    color: '#FFF',
    cursor: 'pointer',
    fontSize: '14px'
  }
};
